import { z } from 'zod';

// Input sanitization helpers
export const sanitizeString = (str: string): string => {
  return str.trim().replace(/[<>\"'&]/g, (match) => {
    const entities: Record<string, string> = {
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '&': '&amp;'
    };
    return entities[match] || match;
  });
};

// Common validation patterns
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const urlRegex = /^https?:\/\/.+/;

// Authentication schemas
export const signInSchema = z.object({
  email: z.string()
    .min(1, "Email is required")
    .max(255, "Email must be less than 255 characters")
    .regex(emailRegex, "Invalid email address")
    .transform(sanitizeString),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password must be less than 128 characters")
});

export const signUpSchema = z.object({
  email: z.string()
    .min(1, "Email is required")
    .max(255, "Email must be less than 255 characters")
    .regex(emailRegex, "Invalid email address")
    .transform(sanitizeString),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password must be less than 128 characters")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, "Password must contain at least one lowercase letter, uppercase letter, and number"),
  displayName: z.string()
    .max(100, "Display name must be less than 100 characters")
    .optional()
    .transform((val) => val ? sanitizeString(val) : undefined)
});

// Recipe schemas
export const ingredientSchema = z.object({
  ingredient_name: z.string()
    .min(1, "Ingredient name is required")
    .max(100, "Ingredient name must be less than 100 characters")
    .transform(sanitizeString),
  quantity: z.number()
    .min(0, "Quantity must be non-negative")
    .max(10000, "Quantity is too large"),
  unit: z.string()
    .max(20, "Unit must be less than 20 characters")
    .transform(sanitizeString),
  notes: z.string()
    .max(500, "Notes must be less than 500 characters")
    .optional()
    .transform((val) => val ? sanitizeString(val) : "")
});

export const instructionSchema = z.object({
  step_number: z.number()
    .int("Step number must be an integer")
    .min(1, "Step number must be at least 1")
    .max(100, "Too many steps"),
  text: z.string()
    .min(1, "Instruction text is required")
    .max(2000, "Instruction text must be less than 2000 characters")
    .transform(sanitizeString),
  timer_seconds: z.number()
    .int("Timer must be in seconds")
    .min(0, "Timer cannot be negative")
    .max(86400, "Timer cannot exceed 24 hours") // 24 hours in seconds
});

export const recipeSchema = z.object({
  title: z.string()
    .min(1, "Recipe title is required")
    .max(200, "Recipe title must be less than 200 characters")
    .transform(sanitizeString),
  description: z.string()
    .max(2000, "Description must be less than 2000 characters")
    .optional()
    .transform((val) => val ? sanitizeString(val) : undefined),
  image_url: z.string()
    .max(2048, "Image URL is too long")
    .regex(urlRegex, "Invalid URL format")
    .optional()
    .or(z.literal("")),
  source_url: z.string()
    .max(2048, "Source URL is too long")
    .regex(urlRegex, "Invalid URL format")
    .optional()
    .or(z.literal("")),
  tags: z.array(z.string()
    .max(50, "Tag must be less than 50 characters")
    .transform(sanitizeString))
    .max(10, "Maximum 10 tags allowed"),
  base_servings: z.number()
    .int("Servings must be a whole number")
    .min(1, "Must serve at least 1 person")
    .max(100, "Cannot serve more than 100 people"),
  prep_time_minutes: z.number()
    .int("Prep time must be in minutes")
    .min(0, "Prep time cannot be negative")
    .max(1440, "Prep time cannot exceed 24 hours"), // 24 hours in minutes
  cook_time_minutes: z.number()
    .int("Cook time must be in minutes")
    .min(0, "Cook time cannot be negative")
    .max(1440, "Cook time cannot exceed 24 hours"),
  difficulty: z.enum(['Easy', 'Medium', 'Hard']),
  ingredients: z.array(ingredientSchema)
    .min(1, "At least one ingredient is required")
    .max(50, "Maximum 50 ingredients allowed"),
  instructions: z.array(instructionSchema)
    .min(1, "At least one instruction is required")
    .max(50, "Maximum 50 instructions allowed")
});

// Pantry item schema
export const pantryItemSchema = z.object({
  ingredient_name: z.string()
    .min(1, "Ingredient name is required")
    .max(100, "Ingredient name must be less than 100 characters")
    .transform(sanitizeString),
  quantity: z.number()
    .min(0, "Quantity must be non-negative")
    .max(10000, "Quantity is too large"),
  unit: z.string()
    .max(20, "Unit must be less than 20 characters")
    .transform(sanitizeString),
  location: z.string()
    .max(50, "Location must be less than 50 characters")
    .transform(sanitizeString),
  expires_on: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
    .optional()
});

// Edge function schemas
export const aiRecipeGenerationSchema = z.object({
  prompt: z.string()
    .min(1, "Prompt is required")
    .max(1000, "Prompt must be less than 1000 characters")
    .transform(sanitizeString)
    .optional(),
  servings: z.number()
    .int("Servings must be a whole number")
    .min(1, "Must serve at least 1 person")
    .max(20, "Cannot serve more than 20 people")
    .default(4),
  dietary: z.array(z.string()
    .max(50, "Dietary restriction must be less than 50 characters")
    .transform(sanitizeString))
    .max(10, "Maximum 10 dietary restrictions")
    .default([]),
  usePantry: z.boolean().default(false),
  pantryItems: z.array(z.object({
    ingredient_name: z.string()
      .max(100, "Ingredient name must be less than 100 characters")
      .transform(sanitizeString),
    quantity: z.number().min(0).max(10000),
    unit: z.string()
      .max(20, "Unit must be less than 20 characters")
      .transform(sanitizeString)
  })).max(100, "Too many pantry items").default([])
});

export const imageGenerationSchema = z.object({
  title: z.string()
    .min(1, "Title is required")
    .max(200, "Title must be less than 200 characters")
    .transform(sanitizeString),
  description: z.string()
    .max(1000, "Description must be less than 1000 characters")
    .optional()
    .transform((val) => val ? sanitizeString(val) : undefined),
  ingredients: z.array(z.object({
    ingredient_name: z.string()
      .max(100, "Ingredient name must be less than 100 characters")
      .transform(sanitizeString)
  })).max(50, "Too many ingredients").default([])
});

export const translateContentSchema = z.object({
  text: z.string()
    .min(1, "Text is required")
    .max(10000, "Text must be less than 10,000 characters"),
  fromLanguage: z.string()
    .length(2, "Language code must be 2 characters")
    .default('en'),
  toLanguage: z.string()
    .length(2, "Language code must be 2 characters")
    .min(1, "Target language is required"),
  type: z.enum(['recipe', 'ingredient', 'instruction', 'general'])
    .default('recipe')
});

export const checkoutSchema = z.object({
  plan: z.enum(['monthly', 'yearly', 'premium_monthly', 'premium_yearly'])
    .default('monthly')
});

// Profile schema
export const profileSchema = z.object({
  display_name: z.string()
    .max(100, "Display name must be less than 100 characters")
    .optional()
    .transform((val) => val ? sanitizeString(val) : undefined),
  avatar_url: z.string()
    .max(2048, "Avatar URL is too long")
    .regex(urlRegex, "Invalid URL format")
    .optional()
    .or(z.literal("")),
  dietary_restrictions: z.array(z.string()
    .max(50, "Dietary restriction must be less than 50 characters")
    .transform(sanitizeString))
    .max(20, "Maximum 20 dietary restrictions")
    .default([]),
  preferred_retailer: z.string()
    .max(50, "Preferred retailer must be less than 50 characters")
    .transform(sanitizeString)
    .default('instacart')
});

export type SignInData = z.infer<typeof signInSchema>;
export type SignUpData = z.infer<typeof signUpSchema>;
export type RecipeData = z.infer<typeof recipeSchema>;
export type PantryItemData = z.infer<typeof pantryItemSchema>;
export type ProfileData = z.infer<typeof profileSchema>;